"use client"

import type { Omit } from "@wagmi/core/internal"
import { type Config, useChainId, useConfig } from "wagmi"

import { Evaluate } from "viem"
import type { GetUnitOfAccountPriceErrorType } from "../actions/getUnitOfAccountPrice"
import {
  type GetUnitOfAccountPriceData,
  type GetUnitOfAccountPriceOptions,
  type GetUnitOfAccountPriceQueryFnData,
  type GetUnitOfAccountPriceQueryKey,
  getUnitOfAccountPriceQueryOptions,
} from "../query"
import type {
  ConfigParameter,
  ExtendedConfig,
  UseQueryParameters,
  UseQueryReturnType,
} from "../types"
import { useQuery } from "../utils"

export type UseUnitOfAccountPriceParameters<
  config extends Config = Config,
  selectData = GetUnitOfAccountPriceData,
> = Evaluate<
  GetUnitOfAccountPriceOptions &
    ConfigParameter<config> & {
      query?:
        | Evaluate<
            Omit<
              UseQueryParameters<
                GetUnitOfAccountPriceQueryFnData,
                GetUnitOfAccountPriceErrorType,
                selectData,
                GetUnitOfAccountPriceQueryKey
              >,
              "gcTime" | "staleTime"
            >
          >
        | undefined
      select?: (data: GetUnitOfAccountPriceQueryFnData) => selectData
    }
>

export type UseUnitOfAccountPriceReturnType<
  selectData = GetUnitOfAccountPriceData,
> = UseQueryReturnType<selectData, GetUnitOfAccountPriceErrorType>

export function useUnitOfAccountPrice<
  config extends ExtendedConfig,
  selectData = GetUnitOfAccountPriceData,
>(
  parameters: UseUnitOfAccountPriceParameters<config, selectData> = {},
): UseUnitOfAccountPriceReturnType<selectData> {
  const { query = {}, unitOfAccount, scopeKey } = parameters

  const config = useConfig(parameters)
  const chainId = useChainId({ config })

  const { queryKey, ...options } = getUnitOfAccountPriceQueryOptions(config, {
    unitOfAccount,
    chainId: parameters.chainId ?? chainId,
    scopeKey,
  })
  const enabled = Boolean(unitOfAccount && (query.enabled ?? true))

  return useQuery({
    ...query,
    ...options,
    queryKey,
    enabled,
    refetchOnMount: false,
    retry: false,
  })
}
